/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch, FaPlus, FaEye, FaPencilAlt, FaTrash,
  FaHeartbeat, FaBell, FaTools, FaSync, FaFilter,
  FaSort, FaSortUp, FaSortDown,
} from "react-icons/fa";
import usePermissions from "../hooks/usePermissions";

/* ─────────────────────────────────────────
   CONFIG
───────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || "/api";

/*
 * Role names must match exactly what your backend stores in the DB
 * super_admin / system_manager → full access
 */
const DEVICE_MANAGERS = ["super_admin", "system_manager"];

const STATUS_OPTIONS = ["all", "online", "offline", "maintenance"];

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const getMe = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}"); }
  catch { return {}; }
};

/*
 * ID mapping:
 *   device.id         → numeric DB primary key  → used for DELETE
 *   device.device_uid → string UID (BTTE...)    → used for navigation, Lambda, backend /uid/ routes
 */

/* ─────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    online:      "bg-green-50 text-green-700 border-green-200",
    offline:     "bg-red-50 text-red-600 border-red-200",
    maintenance: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const dotMap = {
    online:      "bg-green-500",
    offline:     "bg-red-400",
    maintenance: "bg-amber-500",
  };
  const cls    = map[status]    || "bg-gray-100 text-gray-500 border-gray-200";
  const dotCls = dotMap[status] || "bg-gray-400";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
      {status || "—"}
    </span>
  );
};

/* ─────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────── */
const DeleteModal = ({ device, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 backdrop-blur-[2px]">
    <div className="bg-white rounded-xl border border-gray-200 shadow-xl p-6 w-full max-w-sm mx-4">
      <div className="w-10 h-10 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center mb-4">
        <FaTrash size={14} className="text-red-500" />
      </div>
      <h3 className="text-[15px] font-semibold text-gray-900 mb-1">Delete Device</h3>
      <p className="text-[13px] text-gray-500 mb-5">
        Are you sure you want to delete{" "}
        <span className="font-semibold text-gray-800 font-mono">{device?.device_uid}</span>?
        This action cannot be undone.
      </p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel}
          className="px-4 py-2 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm}
          className="px-4 py-2 text-[13px] font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
          Delete
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   QUICK ACTION BUTTON
───────────────────────────────────────── */
const QuickAction = ({ icon: Icon, label, color, onClick, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={label}
    className={`flex items-center justify-center w-7 h-7 rounded-lg border transition-colors
                ${disabled ? "opacity-40 cursor-not-allowed" : color}`}
  >
    <Icon size={11} />
  </button>
);

/* ─────────────────────────────────────────
   SORT ICON
───────────────────────────────────────── */
const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <FaSort size={9} className="text-gray-300 ml-1 inline" />;
  return sortDir === "asc"
    ? <FaSortUp   size={9} className="text-blue-500 ml-1 inline" />
    : <FaSortDown size={9} className="text-blue-500 ml-1 inline" />;
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const AllDevices = () => {
  const navigate = useNavigate();
  const token    = localStorage.getItem("token");

  const [devices,      setDevices]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField,    setSortField]    = useState("device_uid");
  const [sortDir,      setSortDir]      = useState("asc");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast,        setToast]        = useState(null);

  const me              = getMe();
  const isDeviceManager = DEVICE_MANAGERS.includes(me.role);
  const { canView }     = usePermissions();

  /* ── Toast ── */
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Fetch ── */
  const fetchDevices = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError(null);
      const res  = await fetch(`${API}/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch devices");
      const data = await res.json();
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [token]);

  // Fresh telemetry can change status at any moment. Refresh while this page is visible.
  useEffect(() => {
    if (!token) return undefined;

    fetchDevices();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") fetchDevices({ quiet: true });
    };
    const interval = window.setInterval(refreshWhenVisible, 15_000);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [fetchDevices, token]);

  /* ── Delete — uses device.id (numeric DB primary key) ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API}/devices/${deleteTarget.id}`, {
        method:  "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      showToast(`${deleteTarget.device_uid} deleted`);
      fetchDevices();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setDeleteTarget(null);
    }
  };

  /* ── Sort ── */
  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  /* ── Filter + sort ── */
  const filteredDevices = useMemo(() => {
    const q = search.toLowerCase();
    let list = devices.filter((d) => {
      const matchSearch =
        d.device_uid?.toLowerCase().includes(q)   ||
        d.product_name?.toLowerCase().includes(q) ||
        d.product_type?.toLowerCase().includes(q) ||
        d.company_name?.toLowerCase().includes(q) ||
        (!d.company_id && "unassigned".includes(q));
      const matchStatus = statusFilter === "all" || d.status === statusFilter;
      return matchSearch && matchStatus;
    });
    list.sort((a, b) => {
      const av = (a[sortField] || "").toString().toLowerCase();
      const bv = (b[sortField] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [devices, search, statusFilter, sortField, sortDir]);

  /* ── Counts ── */
  const counts = useMemo(() => ({
    total:       devices.length,
    online:      devices.filter((d) => d.status === "online").length,
    offline:     devices.filter((d) => d.status === "offline").length,
    maintenance: devices.filter((d) => d.status === "maintenance").length,
  }), [devices]);

  /* ── Access guard ── */
  if (!canView("devices")) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-[14px] font-semibold text-red-600">Access Denied</p>
          <p className="text-[12px] text-gray-400 mt-1">You don't have permission to view devices.</p>
        </div>
      </div>
    );
  }

  /* ── TH helper ── */
  const Th = ({ label, field, className = "" }) => (
    <th
      onClick={() => field && handleSort(field)}
      className={`px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-[0.6px]
                  ${field ? "cursor-pointer select-none hover:text-gray-600 transition-colors" : ""}
                  ${className}`}
    >
      {label}
      {field && <SortIcon field={field} sortField={sortField} sortDir={sortDir} />}
    </th>
  );

  /* ─────────────────────────────────────
     RENDER
  ───────────────────────────────────── */
  return (
    <>
      {/* Toast */}
      {toast && (
        <div className={`
          fixed top-5 right-5 z-50 flex items-center gap-2.5 px-4 py-2.5
          rounded-lg border shadow-lg text-[13px] font-medium
          ${toast.type === "error"
            ? "bg-red-50 border-red-200 text-red-700"
            : "bg-green-50 border-green-200 text-green-700"}
        `}>
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <DeleteModal
          device={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="space-y-4">

        {/* ── Header ── */}
        <div className="cb-hero-panel rounded-lg px-6 py-5 text-white">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
              <p className="text-xs font-bold uppercase text-emerald-100">Fleet Operations</p>
              <h1 className="mt-1 text-3xl font-black tracking-tight">Devices</h1>
              <p className="mt-2 max-w-2xl text-sm font-medium text-emerald-50/85">
              {isDeviceManager
                ? "Manage and monitor all IoT devices across all companies"
                : "IoT devices assigned to your account"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchDevices}
              disabled={loading}
                className="flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/12 px-3 py-2 text-[12px] font-bold text-white transition hover:bg-white/18 disabled:opacity-50"
            >
                <FaSync size={10} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            {/* Add Device — managers only */}
            {isDeviceManager && (
              <button
                onClick={() => navigate("/dashboard/devices/add")}
                  className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-[12px] font-bold text-emerald-700 transition hover:bg-emerald-50"
              >
                <FaPlus size={10} /> Add Device
              </button>
            )}
          </div>
          </div>
        </div>

        {/* ── Stat Pills ── */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            { label: "Total",       count: counts.total,       color: "text-slate-700", filter: "all"         },
            { label: "Online",      count: counts.online,      color: "text-emerald-700", filter: "online"      },
            { label: "Offline",     count: counts.offline,     color: "text-red-600", filter: "offline"    },
            { label: "Maintenance", count: counts.maintenance, color: "text-amber-700", filter: "maintenance" },
          ].map(({ label, count, color, filter }) => (
            <button
              key={filter}
              onClick={() => setStatusFilter(filter)}
              className={`cb-stat-card rounded-lg p-4 text-left transition hover:-translate-y-0.5 ${statusFilter === filter ? "ring-2 ring-emerald-500/25" : ""}`}
            >
              <span className="cb-section-title">{label}</span>
              <span className={`mt-2 block text-2xl font-black ${color}`}>{count}</span>
            </button>
          ))}
        </div>

        {/* ── Toolbar ── */}
        <div className="cb-panel flex items-center gap-2 flex-wrap rounded-lg p-3">
          <div className="relative">
            <FaSearch size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by UID, product or company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-4 py-1.5 text-[13px] rounded-lg w-72
                         bg-white border border-gray-200 text-gray-700 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
            />
          </div>

          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
            <FaFilter size={10} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-[12px] text-gray-600 bg-transparent border-none outline-none cursor-pointer font-medium"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {(search || statusFilter !== "all") && (
            <button
              onClick={() => { setSearch(""); setStatusFilter("all"); }}
              className="text-[11px] text-gray-400 hover:text-gray-700 underline transition-colors"
            >
              Clear filters
            </button>
          )}

          <span className="ml-auto text-[11px] text-gray-400 font-medium">
            Showing {filteredDevices.length} of {devices.length}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="cb-table-shell">

          {error && (
            <div className="px-5 py-3 bg-red-50 border-b border-red-200 text-[13px] text-red-600 font-medium">
              Error: {error} —{" "}
              <button onClick={fetchDevices} className="underline hover:no-underline">Retry</button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <Th label="Device UID" field="device_uid"   />
                  <Th label="Product"    field="product_name" />
                  <Th label="Company"    field="company_name" />
                  <Th label="Status"     field="status"       />
                  <Th label="Last Seen"  field="last_seen"    />
                  <Th label="Actions"    className="text-right" />
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-t border-gray-50">
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-5 py-3.5">
                          <div className="h-3 bg-gray-100 rounded animate-pulse"
                               style={{ width: `${60 + (j * 7) % 30}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-14 text-center">
                      <p className="text-[13px] text-gray-400 font-medium">No devices found</p>
                      {(search || statusFilter !== "all") && (
                        <p className="text-[12px] text-gray-300 mt-1">Try adjusting your search or filters</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredDevices.map((device) => (
                    <tr
                      key={device.id ?? device.device_uid}
                      className="border-t border-gray-50 hover:bg-gray-50/60 transition-colors"
                    >
                      {/* Device UID */}
                      <td className="px-5 py-3.5">
                        <span className="text-[12px] font-mono font-semibold text-gray-800">
                          {device.device_uid}
                        </span>
                      </td>

                      {/* Product — prefers joined product_name, falls back to product_type */}
                      <td className="px-5 py-3.5 text-[13px] text-gray-500">
                        {device.product_name || device.product_type || "—"}
                      </td>

                      {/* Company — prefers joined company_name, falls back to raw id */}
                      <td className="px-5 py-3.5 text-[13px] text-gray-500">
                        {device.company_name || (device.company_id ? `Company ${device.company_id}` : "Unassigned")}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-3.5">
                        <StatusBadge status={device.status} />
                      </td>

                      {/* Last Seen */}
                      <td className="px-5 py-3.5 text-[12px] font-mono text-gray-400">
                        {device.last_seen
                          ? new Date(device.last_seen).toLocaleString([], {
                              month: "short", day: "numeric",
                              hour: "2-digit", minute: "2-digit",
                            })
                          : "—"}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">

                          {/*
                           * VIEW — uses device_uid
                           * Route: /dashboard/devices/:uid
                           * Backend: GET /api/devices/uid/:uid
                           */}
                          <QuickAction
                            icon={FaEye}
                            label="View device"
                            color="bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100"
                            disabled={!device.device_uid}
                            onClick={() => navigate(`/dashboard/devices/${device.device_uid}`)}
                          />

                          {/*
                           * HEALTH — uses device_uid (Lambda also uses uid)
                           */}
                          <QuickAction
                            icon={FaHeartbeat}
                            label="Device health"
                            color="bg-green-50 border-green-200 text-green-600 hover:bg-green-100"
                            onClick={() => navigate(`/dashboard/device-health?device=${device.device_uid}`)}
                          />

                          {/* ALERTS — uses device_uid */}
                          <QuickAction
                            icon={FaBell}
                            label="View alerts"
                            color="bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100"
                            onClick={() => navigate(`/dashboard/alerts?device=${device.device_uid}`)}
                          />

                          {/* MAINTENANCE — uses device_uid */}
                          <QuickAction
                            icon={FaTools}
                            label="Maintenance"
                            color="bg-purple-50 border-purple-200 text-purple-600 hover:bg-purple-100"
                            onClick={() => navigate(`/dashboard/maintenance?device=${device.device_uid}`)}
                          />

                          {/*
                           * EDIT — managers only, uses device_uid
                           * Route: /dashboard/devices/edit/:uid
                           * Backend: PUT /api/devices/uid/:uid
                           */}
                          {isDeviceManager && (
                            <QuickAction
                              icon={FaPencilAlt}
                              label="Edit device"
                              color="bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                              disabled={!device.device_uid}
                              onClick={() => navigate(`/dashboard/devices/edit/${device.device_uid}`)}
                            />
                          )}

                          {/*
                           * DELETE — managers only, uses device.id (numeric DB PK)
                           * Backend: DELETE /api/devices/:id
                           */}
                          {isDeviceManager && (
                            <QuickAction
                              icon={FaTrash}
                              label="Delete device"
                              color="bg-red-50 border-red-200 text-red-500 hover:bg-red-100"
                              onClick={() => setDeleteTarget(device)}
                            />
                          )}

                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {!loading && filteredDevices.length > 0 && (
            <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <span className="text-[11px] text-gray-400">
                {filteredDevices.length} device{filteredDevices.length !== 1 ? "s" : ""}
              </span>
              <span className="text-[11px] text-gray-400">
                {counts.online} online · {counts.maintenance} maintenance · {counts.offline} offline
              </span>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default AllDevices;
